/**
 * CategoryRemapNoticeBanner — Phase 70I.5b
 *
 * A one-time, dismissible banner that appears on Dashboard + Transactions
 * after a superadmin has run the category remap for this user.
 *
 * Lifecycle
 *  - Backend `/api/categories/remap/notice` returns { should_show,
 *    renamed_count, flagged_count, last_remapped_at }.
 *  - When `should_show=true`, we render a single brand-toned card.
 *  - Two CTAs:
 *      • "Review {flagged_count} item(s)" → navigates to the Phase 70J
 *        review tray (route `/review-uncategorized`).
 *      • "Dismiss"                       → POSTs to /notice/dismiss and
 *                                          hides locally (optimistic).
 *  - After dismissal, the backend will keep returning should_show=false
 *    until a fresh remap bumps the timestamp again.
 *
 * Mount surfaces
 *  - Dashboard (top of grid)
 *  - Transactions (top of page)
 *
 * Resilience
 *  - Any error from the notice endpoint silently hides the banner —
 *    this is a UX nicety, never a blocker for the page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, X, AlertTriangle, ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import TAXONOMY from '@/lib/taxonomyExpense';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function freshToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch { return null; }
}

export default function CategoryRemapNoticeBanner() {
  const [notice, setNotice]   = useState(null);   // { should_show, renamed_count, flagged_count, ... }
  const [hidden, setHidden]   = useState(false);  // optimistic dismissal
  const [dismissing, setDismissing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const token = await freshToken();
      if (!token) return;
      const { data } = await axios.get(
        `${BACKEND_URL}/api/categories/remap/notice`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotice(data);
    } catch {
      setNotice({ should_show: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = useCallback(async () => {
    setHidden(true); // optimistic
    setDismissing(true);
    try {
      const token = await freshToken();
      await axios.post(
        `${BACKEND_URL}/api/categories/remap/notice/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      // No-op — banner is already hidden; user can refresh to retry if
      // the dismissal failed silently.
    } finally {
      setDismissing(false);
    }
  }, []);

  if (hidden || !notice?.should_show) return null;

  const flagged = Number(notice.flagged_count || 0);
  const renamed = Number(notice.renamed_count || 0);

  return (
    <div
      className={[
        // Brand-toned card. Soft gold tint so it reads as a friendly
        // notice, not an alert. Slight border for definition in light
        // mode; deeper in dark mode via tokenised variables.
        'relative rounded-2xl border border-[hsl(var(--gold))]/30',
        'bg-gradient-to-r from-[hsl(var(--gold))]/[0.08] to-transparent',
        'px-4 py-3.5 sm:px-5 sm:py-4',
        'flex flex-col sm:flex-row sm:items-center sm:gap-4',
      ].join(' ')}
      data-testid="category-remap-notice-banner"
    >
      {/* Icon + text */}
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="shrink-0 h-9 w-9 rounded-full bg-[hsl(var(--gold))]/15 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-[hsl(var(--gold))]" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm">We've rearranged your categories</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            We migrated your transactions into a cleaner {TAXONOMY.categories.length}-category system.
            {renamed > 0 && (
              <span> <strong className="text-foreground/80">{renamed.toLocaleString()}</strong> were renamed automatically.</span>
            )}
            {flagged > 0 ? (
              <span>
                {' '}<strong className="text-foreground/80">{flagged.toLocaleString()}</strong>{' '}
                couldn't be auto-matched — would you like to review them?
              </span>
            ) : (
              <span> Nothing needs your review.</span>
            )}
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex items-center gap-2 mt-3 sm:mt-0 sm:shrink-0">
        {flagged > 0 ? (
          <Button
            size="sm"
            className="rounded-xl"
            onClick={() => {
              // Don't auto-dismiss when the user clicks Review — they may
              // come back to this same banner if they don't finish the tray.
              navigate('/review-uncategorized');
            }}
            data-testid="category-remap-notice-review"
          >
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            Review {flagged} item{flagged === 1 ? '' : 's'}
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="rounded-xl"
          onClick={dismiss}
          disabled={dismissing}
          data-testid="category-remap-notice-dismiss"
        >
          <X className="h-3.5 w-3.5 mr-1" />
          {flagged > 0 ? 'Not now' : 'Got it'}
        </Button>
      </div>
    </div>
  );
}
